import React, {createElement, useContext, useState} from "react";
import useDemoInfo from "../../utils/useDemoInfo";
import FormContext, { useForm } from "../../components/FormContext";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {ContactSection} from '../../styles/ContactStyles';
import {Link} from "gatsby";

const translations = {
  en: {
    Submit: 'Submit',
    RequiredField: 'This field is required',
    RequestError: 'An error occurred while sending the request',
    RequestErrorContact: 'Sorry. We are not able to send your email. Please contact us on',
    RequestSuccess: 'Thank you for submitting the contact form. We will review your message and get back to you shortly.',
    InvalidEmail: 'Invalid email address',
  },
  fr: {
    Submit: 'Soumettre',
    RequiredField: 'Ce champ est obligatoire',
    RequestError: "Une erreur s'est produite lors de l'envoi de la demande",
    RequestErrorContact: 'Désolé, nous ne pouvons pas envoyer votre courriel. Veuillez nous contacter à l\'adresse',
    RequestSuccess: "Merci d'avoir soumis le formulaire de contact. Nous examinerons votre message et vous répondrons sous peu.",
    InvalidEmail: 'Adresse e-mail non valide',
  },
};

function getTranslation(key, locale) {
  return translations[locale] && translations[locale][key] ? translations[locale][key] : translations['en'][key];
};

const isValidEmail = (email) => {
  if (!email) return false;
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

export default function ContactForm({ data, pageContext, grid }) {

  const { executeRecaptcha } = useGoogleReCaptcha();
  const regulationContent = data.regulations[pageContext][0]._rawChildren;
  const useFormContext  = useContext(FormContext) || {};
  const regulationLanguage = pageContext === 'en' ? '_rawEn' : '_rawFr';

  const { state, handleChange} = useForm();
  const [formSent, setFormSent] = useState(false);
  const [formContactInfo, setFormContactInfo] = useState(false);
  const [reqCheckbox, setReqCheckbox] = useState(false);
  const [reqEmail, setReqEmail] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const {fullname, email, phone, msg, accept} = state;
  const { loading, message, error } = useDemoInfo({ values: useFormContext.state, locale: pageContext});

  function getMarkedText(data, languageKey) {
    const blocks = data.regulations[languageKey];
    return blocks.map(block => {
      return block.children.map(child => {
        if (child.marks.length > 0) {
          const markValues = child.marks.map(markId => {
            const markDef = block.markDefs.find(def => def._key === markId);
            return markDef ? markDef.href : null;
          })[0];
          return { text: child.text, markValues };
        }
        return { text: child.text };
      });
    })[0];
  }

  const resetForm = () => {
    handleChange("fullname", "");
    handleChange("email", "");
    handleChange("phone", "");
    handleChange("msg", "");
    handleChange("accept", false);
  }

  const handleRecaptcha = async (event) => {
    event.preventDefault();

    const emailValue = useFormContext.state?.email ?? email;
    const acceptValue = useFormContext.state?.accept ?? accept;

    let hasError = false;

    if (!acceptValue) {
      setReqCheckbox(true);
      hasError = true;
    } else {
      setReqCheckbox(false);
    }

    if (!emailValue) {
      setReqEmail(true);
      setEmailError(false);
      hasError = true;
    } else {
      setReqEmail(false);
    }

    if (emailValue && !isValidEmail(emailValue)) {
      setEmailError(true);
      hasError = true;
    } else if (emailValue && isValidEmail(emailValue)) {
      setEmailError(false);
    }

    if (hasError) {
      return;
    }

    try {
      const token = await executeRecaptcha('homepage');

      const body = {
        email: emailValue,
        template_id: process.env.GATSBY_CBP_TEMPLATE_ID,
        "g-recaptcha-response": token,
        params: {
          fullname: useFormContext.state?.fullname ?? fullname,
          phone: useFormContext.state?.phone ?? phone,
          msg: useFormContext.state?.msg ?? msg,
        }
      };

      const emailSend = await fetch(process.env.GATSBY_CBP_ENDPOINT + "email/send", {
        method: "POST",
        headers: {
          'X-API-KEY': process.env.GATSBY_CBP_API_KEY,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(body),
      });

      if(emailSend.ok) {
        setFormSent(true);
        setFormContactInfo(false);
        resetForm();
      } else {
        setFormSent(false);
        setFormContactInfo(true);
      }
    } catch (err) {
      setFormSent(false);
      setFormContactInfo(true);
    }
  }

  return (
    <ContactSection id={`grid-${grid}`}>
      <div>
        <h2>{data.title[pageContext]}</h2>
        <p>{data.description[pageContext]}</p>
      </div>
      <div>
        {!formSent && !formContactInfo && (
          <form onSubmit={handleRecaptcha} noValidate>
            <fieldset>
              <legend className="hidden">Contact Form</legend>
              <div className="field">
                <label htmlFor="input_fullname">Full name</label>
                <input
                  id="input_fullname"
                  type="text"
                  value={fullname}
                  onChange={(e) => handleChange("fullname", e.currentTarget.value)}
                />
              </div>
              <div className="field_2cols">
                <div className={`field ${(reqEmail || emailError) ? 'invalid' : ''}`}>
                  <label htmlFor="input_email">
                    <span>Email *</span>
                    {reqEmail && <p className="invalid">{getTranslation('RequiredField', pageContext)}</p>}
                    {emailError && !reqEmail && <p className="invalid">{getTranslation('InvalidEmail', pageContext)}</p>}
                  </label>
                  <input
                    id="input_email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      handleChange("email", value);

                      if (!value) {
                        setReqEmail(true);
                        setEmailError(false);
                      } else {
                        setReqEmail(false);
                        setEmailError(!isValidEmail(value));
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.currentTarget.value;
                      if (value && !isValidEmail(value)) {
                        setEmailError(true);
                      }
                    }}
                  />
                </div>
                <div className="field">
                  <label htmlFor="input_phone">Phone</label>
                  <input
                    id="input_phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => handleChange("phone", e.currentTarget.value)}
                  />
                </div>
              </div>
              <div className="field">
                <label htmlFor="input_msg">Message</label>
                <textarea
                  id="input_msg"
                  value={msg}
                  onChange={(e) => handleChange("msg", e.currentTarget.value)}
                />
              </div>
              <div className={`field regulation ${reqCheckbox ? 'invalid' : ''}`}>
                <label htmlFor="input_accept">
                  <span>
                    {data.regulations[regulationLanguage] ?
                      getMarkedText(data, regulationLanguage).map((item, index) => {
                        if (item.markValues) {
                          return (
                            <Link
                              key={`regulation_link_${index}`}
                              to={pageContext === 'en' ? `/${item.markValues}` : `/${pageContext + item.markValues}`}
                            >
                              {item.text}
                            </Link>
                          )
                        }
                        return (
                          <span key={`regulation_text_${index}`}>{item.text}</span>
                        )
                      }): regulationContent &&
                      regulationContent.map((item, index) => (
                        createElement(item._type, {key: `accept_${index}`}, item.text)
                      ))
                    }
                  </span>
                  {reqCheckbox && <p className="invalid">{getTranslation('RequiredField', pageContext)}</p>}
                </label>
                <input
                  type="checkbox"
                  id="input_accept"
                  checked={!!accept}
                  onChange={(e) => {
                    handleChange("accept", e.currentTarget.checked);
                    setReqCheckbox(!e.currentTarget.checked)
                  }}
                  required
                />
              </div>
              <button type="submit" className="button primary" disabled={loading}>
                {getTranslation('Submit', pageContext)}
              </button>
            </fieldset>
          </form>
        )}

        {formSent && !error && !formContactInfo && (
          <div>
            <h5>{getTranslation('RequestSuccess', pageContext)}</h5>
            <p>{message}</p>
          </div>
        )}

        {((error && formSent && message) || formContactInfo) && (
          <div>
            <h5>{getTranslation('RequestError', pageContext)}</h5>
            <p>{getTranslation('RequestErrorContact', pageContext)}
              &nbsp;<a href="mailto:contact@abdigital.enterprises" style={{textDecoration: "underline"}}>
                contact@abdigital.enterprises
              </a>
            </p>
          </div>
        )}
      </div>
    </ContactSection>
  )
}